import { isDefined } from "~/shared/common";
import type { Option, OptionUpdate, Property } from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid } from "~/utils/common";
import { makeHierarchicalOptionComparator, optionComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToOptions: Map<string, Option>;
  _sortedOptionDuids: string[];
};

export const state: State = {
  _duidsToOptions: new Map(),
  _sortedOptionDuids: [],
};

export type Getters = {
  optionList: Option[];
  getOptionList: (property: Property) => Option[];
  getOptionByDuid: (duid: string) => Option | undefined;
  getOptionsByDuidsOrdered: (duids: string[]) => Option[];
  getOptionByTitle: (title: string, property: Property, parentDuid?: string | null) => Option | undefined;
};

export type Actions = {
  createOption: (
    title: string,
    property: Property,
    partialOption?: Partial<Option>,
    options?: { awaitBackend?: boolean }
  ) => Promise<Option | undefined>;
  updateOption: (update: OptionUpdate, options?: { awaitBackend?: boolean }) => Promise<Option | undefined>;
  deleteOption: (option: Option, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createOptionNoBackend: (title: string, property: Property, partialOption?: Partial<Option>) => Option | undefined;
  $setOptions: (options: Option[]) => void;
  $createOrUpdateOption: (option: Option) => void;
  $deleteOption: (option: Option) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  optionList() {
    return this._sortedOptionDuids.map((e) => this._duidsToOptions.get(e)).filter(isDefined);
  },
  getOptionList() {
    return (property) => {
      const propertyOptions = this.optionList.filter((e) => e.propertyDuid === property.duid);
      return [...propertyOptions].sort(makeHierarchicalOptionComparator(propertyOptions));
    };
  },
  getOptionByDuid() {
    return (duid) => this._duidsToOptions.get(duid);
  },
  getOptionsByDuidsOrdered() {
    return (duids) => this.optionList.filter((e) => duids.includes(e.duid));
  },
  getOptionByTitle() {
    return (title, property, parentDuid) => {
      const normTitle = title.trim().toLowerCase();
      return this.getOptionList(property).find(
        (e: Option) => (parentDuid === undefined || e.parentDuid === parentDuid) && e.title.toLowerCase() === normTitle
      );
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createOption(title, property, partialOption = {}, options = {}) {
    const { awaitBackend = false } = options;
    const option = this.$createOptionNoBackend(title, property, partialOption);
    if (!option) {
      return undefined;
    }

    const backendAction = this.$backend.option.create(option);
    if (awaitBackend) {
      await backendAction;
    }
    return option;
  },
  async updateOption(update, options = {}) {
    const { awaitBackend = false } = options;
    const option = this.getOptionByDuid(update.duid);
    if (!option) {
      return undefined;
    }

    Object.assign(option, update);
    updateInSorted(this._duidsToOptions, this._sortedOptionDuids, option.duid, optionComparator);

    const backendAction = this.$backend.option.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return option;
  },
  async deleteOption(option, options = {}) {
    const { awaitBackend = false } = options;

    this.$updateLayoutsRemoveEntityDuidOrFiltersNoBackend([option.duid], [option.propertyDuid]);
    this.$updateDartboardsPropertyDefaultNoBackend([option.duid], [option.propertyDuid]);

    this.$deleteOption(option);

    const backendAction = this.$backend.option.delete(option.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $createOptionNoBackend(title, property, partialOption = {}) {
    const { parentDuid } = partialOption;
    if (title !== "" && this.getOptionByTitle(title, property, parentDuid) !== undefined) {
      return undefined;
    }

    const option: Option = {
      duid: makeDuid(),
      propertyDuid: property.duid,
      parentDuid: null,
      title: title.trim(),
      colorHex: makeRandomColorHex(),
      ...partialOption,
    };

    this.$createOrUpdateOption(option);

    return option;
  },
  $setOptions(options) {
    setObjectMapAndSorted(this._duidsToOptions, this._sortedOptionDuids, options, optionComparator);
  },
  $createOrUpdateOption(option) {
    const currentOption = this.getOptionByDuid(option.duid);

    if (!currentOption) {
      this._duidsToOptions.set(option.duid, option);
    } else {
      Object.assign(currentOption, option);
    }
    updateInSorted(this._duidsToOptions, this._sortedOptionDuids, option.duid, optionComparator);
  },
  $deleteOption(option) {
    this._duidsToOptions.delete(option.duid);
    removeFromSorted(this._sortedOptionDuids, option.duid);
  },
};

export { actions, getters };
